import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MailIcon, ArrowLeftIcon } from "lucide-react";
import { z } from "zod";

import { cn } from "./lib/cn";
import { AuthHttpError } from "./auth-http-error";
import { authRequestPasswordReset, type MailLanguage } from "./auth-fetch";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";

export type RequestPasswordResetFormMessages = {
  errors: {
    emailRequired: string;
    emailInvalid: string;
    tooManyResetRequests: string;
    tooManyResetRequestsDesc: string;
    resetRequestFailed: string;
    resetRequestFailedDesc: string;
  };
  resetRequest: {
    title: string;
    subtitle: string;
    submitButton: string;
    backToLogin: string;
    success: string;
    successDesc: string;
  };
  login: {
    termsOfService: string;
    termsOfServiceLink: string;
    and: string;
    privacyPolicyLink: string;
    agreeToSuffix: string;
  };
};

type RequestPasswordResetFormProps = {
  authBaseUrl: string;
  messages: RequestPasswordResetFormMessages;
  language: MailLanguage;
  heroImageSrc?: string;
  privacyPolicyHref: string;
  onNavigateToLogin: () => void;
  onSuccess?: () => void;
  footerSlot?: React.ReactNode;
  className?: string;
};

export function RequestPasswordResetForm({
  authBaseUrl,
  messages: m,
  language,
  heroImageSrc,
  privacyPolicyHref,
  onNavigateToLogin,
  onSuccess,
  footerSlot,
  className,
  ...props
}: RequestPasswordResetFormProps &
  Omit<React.ComponentProps<"div">, keyof RequestPasswordResetFormProps>) {
  const formSchema = z.object({
    email: z
      .string({ message: m.errors.emailRequired })
      .email({ message: m.errors.emailInvalid }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: { email: "" },
  });

  const mutation = useMutation({
    mutationFn: ({ email }: { email: string }) =>
      authRequestPasswordReset(authBaseUrl, { to: email, language }),
    onError: (error: unknown) => {
      if (error instanceof AuthHttpError && error.status === 429) {
        toast.warning(m.errors.tooManyResetRequests, {
          icon: <MailIcon />,
          description: m.errors.tooManyResetRequestsDesc,
          richColors: true,
        });
      } else {
        toast.error(m.errors.resetRequestFailed, {
          description: m.errors.resetRequestFailedDesc,
          richColors: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(m.resetRequest.success, {
        description: m.resetRequest.successDesc,
        richColors: true,
      });
      onSuccess?.();
    },
  });

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Form {...form}>
            <form
              className="p-6 md:p-8"
              onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
            >
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold">{m.resetRequest.title}</h1>
                  <p className="text-muted-foreground text-balance">
                    {m.resetRequest.subtitle}
                  </p>
                </div>
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="m@example.com"
                            autoComplete="email"
                            tabIndex={1}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button type="submit" className="w-full" tabIndex={2}>
                  {m.resetRequest.submitButton}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={onNavigateToLogin}
                  tabIndex={3}
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  {m.resetRequest.backToLogin}
                </Button>
                {footerSlot ? (
                  <div className="flex justify-center pt-2">{footerSlot}</div>
                ) : null}
              </div>
            </form>
          </Form>
          {heroImageSrc ? (
            <div className="bg-muted relative hidden md:block">
              <img
                src={heroImageSrc}
                alt=""
                className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
      <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
        {m.login.termsOfService}{" "}
        <a href="#">{m.login.termsOfServiceLink}</a> {m.login.and}{" "}
        <a href={privacyPolicyHref} target="_blank" rel="noreferrer">
          {m.login.privacyPolicyLink}
        </a>{" "}
        {m.login.agreeToSuffix}.
      </div>
    </div>
  );
}
